import React, { useState, useCallback, useEffect, useMemo } from "react";
import { getDatabase, ref, set, serverTimestamp, update } from "firebase/database";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import app from "../../../firebase";
import { useLocation, useNavigate } from "react-router-dom";

const db = getDatabase(app);
const storage = getStorage(app);

const AddDecorativeProductForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const sellerEmail = localStorage.getItem("architectEmail");
  const architectId = localStorage.getItem("architectId");
  const productToEdit = location.state?.productToEdit;
  const isEditMode = !!productToEdit;

  // Predefined categories wrapped in useMemo
  const predefinedCategories = useMemo(() => [
    { value: "abstract-modern", label: "Abstract Modern" },
    { value: "animal-birds", label: "Animal / Birds" },
    { value: "decorative-items", label: "Decorative Items" },
    { value: "buddha", label: "Buddha" },
    { value: "ganesha", label: "Ganesha" },
    { value: "krishna", label: "Krishna" },
    { value: "love-couple", label: "Love Couple" },
    { value: "god-goddess", label: "God/Goddess" },
  ], []);

  const materials = useMemo(() => [
    "Metal", "Wood", "Ceramic", "Resin", "Stone", 
    "Glass", "Clay", "Fiber", "Marble", "Terracotta",
    "Brass", "Bronze", "Copper", "Iron", "Jute",
    "Bamboo", "Crystal", "Porcelain", "Other"
  ], []);

  const colors = useMemo(() => [
    "Gold", "Silver", "Bronze", "Black", "White",
    "Red", "Blue", "Green", "Yellow", "Multi-color",
    "Natural Wood", "Antique", "Patina", "Metallic",
    "Ivory", "Brown", "Beige", "Other"
  ], []);

  const sizes = useMemo(() => [
    "Small (under 6 inches)", "Medium (6-12 inches)", 
    "Large (12-24 inches)", "Extra Large (24+ inches)",
    "Set", "Custom"
  ], []);

  const [formData, setFormData] = useState({
    category: "",
    customCategory: "",
    productName: "",
    material: "",
    color: "",
    size: "",
    theme: "",
    isHandmade: false,
    isAntique: false,
    isEcoFriendly: false,
    quantities: [
      {
        quantity: "",
        price: "",
        discountPrice: "",
      },
    ],
    description: "",
    images: [],
  });

  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (isEditMode && productToEdit) {
      const { categoryKey, productName, material, color, size, theme, 
              isHandmade, isAntique, isEcoFriendly, variants, description, images } = productToEdit;
      
      const isCustomCategory = !predefinedCategories.some(cat => cat.value === categoryKey);
      
      let quantities = [];
      if (variants && variants.length > 0) {
        quantities = variants.map(variant => ({
          quantity: variant.quantity || "",
          price: variant.price || "",
          discountPrice: variant.discountPrice || "",
        }));
      } else {
        quantities = [{
          quantity: productToEdit.quantity || "",
          price: productToEdit.price || "",
          discountPrice: productToEdit.discountPrice || "",
        }];
      }

      setFormData({
        category: isCustomCategory ? "custom" : categoryKey,
        customCategory: isCustomCategory ? categoryKey : "",
        productName,
        material: material || "",
        color: color || "",
        size: size || "",
        theme: theme || "",
        isHandmade: isHandmade || false,
        isAntique: isAntique || false,
        isEcoFriendly: isEcoFriendly || false,
        quantities,
        description: description || "",
        images: [],
      });

      setShowCustomCategory(isCustomCategory);

      if (images) {
        setExistingImages(images);
        setImagePreviews(images);
      }
    }
  }, [isEditMode, productToEdit, predefinedCategories]);

  const validateProductName = (name) => {
    const invalidChars = ['.', '$', '#', '[', ']', '/'];
    const hasInvalidChars = invalidChars.some(char => name.includes(char));
    
    if (hasInvalidChars) {
      return {
        isValid: false,
        message: `Product name cannot contain these characters: . $ # [ ] /`
      };
    }
    
    if (name.trim().length === 0) {
      return {
        isValid: false,
        message: 'Product name is required'
      };
    }
    
    if (name.trim().length < 3) {
      return {
        isValid: false,
        message: 'Product name must be at least 3 characters'
      };
    }
    
    return { isValid: true };
  };

  const validateCustomCategory = (category) => {
    if (!category.trim()) {
      return {
        isValid: false,
        message: 'Custom category name is required'
      };
    }
    
    if (category.trim().length < 3) {
      return {
        isValid: false,
        message: 'Category name must be at least 3 characters'
      };
    }
    
    const formattedCategory = category.trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    
    const invalidChars = ['.', '$', '#', '[', ']', '/', '\\'];
    const hasInvalidChars = invalidChars.some(char => formattedCategory.includes(char));
    
    if (hasInvalidChars) {
      return {
        isValid: false,
        message: 'Category name cannot contain special characters: . $ # [ ] / \\'
      };
    }
    
    return {
      isValid: true,
      formattedValue: formattedCategory
    };
  };

  const validateQuantityFields = (quantities) => {
    const errors = {};
    let isValid = true;

    quantities.forEach((qty, index) => {
      if (!qty.quantity || isNaN(qty.quantity)) {
        errors[`quantity-${index}`] = 'Quantity is required and must be a number';
        isValid = false;
      } else if (parseInt(qty.quantity) <= 0) {
        errors[`quantity-${index}`] = 'Quantity must be greater than 0';
        isValid = false;
      }

      if (!qty.price || isNaN(qty.price)) {
        errors[`price-${index}`] = 'Price is required and must be a number';
        isValid = false;
      } else if (parseFloat(qty.price) <= 0) {
        errors[`price-${index}`] = 'Price must be greater than 0';
        isValid = false;
      }

      if (qty.discountPrice && !isNaN(qty.discountPrice)) {
        if (parseFloat(qty.discountPrice) >= parseFloat(qty.price)) {
          errors[`discountPrice-${index}`] = 'Discount price must be less than regular price';
          isValid = false;
        } else if (parseFloat(qty.discountPrice) <= 0) {
          errors[`discountPrice-${index}`] = 'Discount price must be greater than 0';
          isValid = false;
        }
      }
    });

    return { isValid, errors };
  };

  const handleCategoryChange = (e) => {
    const { value } = e.target;
    const isCustom = value === "custom";
    
    setShowCustomCategory(isCustom);
    
    setFormData({
      ...formData,
      category: value,
      customCategory: isCustom ? formData.customCategory : "",
      productName: "",
      material: "",
      color: "",
      size: "",
      theme: "",
      quantities: [
        {
          quantity: "",
          price: "",
          discountPrice: "",
        },
      ],
      description: "",
      images: [],
    });
    setImagePreviews([]);
    setExistingImages([]);
    setImagesToDelete([]);
    setError(null);
    setSuccess(null);
    setFieldErrors({});
  };

  const handleCustomCategoryChange = (e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      customCategory: value
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'productName') {
      const validation = validateProductName(value);
      if (!validation.isValid) {
        setFieldErrors({
          ...fieldErrors,
          productName: validation.message
        });
      } else if (fieldErrors.productName) {
        const newErrors = {...fieldErrors};
        delete newErrors.productName;
        setFieldErrors(newErrors);
      }
    }
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleQuantityChange = (index, e) => {
    const { name, value } = e.target;
    const newQuantities = [...formData.quantities];
    newQuantities[index] = { ...newQuantities[index], [name]: value };
    
    // Validate discount price if it exists
    if (name === 'discountPrice' || name === 'price') {
      const quantityErrors = {...fieldErrors};
      
      if (newQuantities[index].discountPrice && newQuantities[index].price) {
        if (parseFloat(newQuantities[index].discountPrice) >= parseFloat(newQuantities[index].price)) {
          quantityErrors[`discountPrice-${index}`] = 'Discount price must be less than regular price';
        } else {
          delete quantityErrors[`discountPrice-${index}`];
        }
      }
      
      setFieldErrors(quantityErrors);
    }
    
    setFormData({ ...formData, quantities: newQuantities });
  };

  const addQuantityField = () => {
    setFormData({
      ...formData,
      quantities: [
        ...formData.quantities,
        {
          quantity: "",
          price: "",
          discountPrice: "",
        },
      ],
    });
  };

  const removeQuantityField = (index) => {
    const newQuantities = formData.quantities.filter((_, i) => i !== index);
    
    // Remove any errors associated with this quantity field
    const newErrors = {...fieldErrors};
    Object.keys(newErrors).forEach(key => {
      if (key.startsWith(`quantity-${index}`) || 
          key.startsWith(`price-${index}`) || 
          key.startsWith(`discountPrice-${index}`)) {
        delete newErrors[key];
      }
    });
    
    setFieldErrors(newErrors);
    setFormData({ ...formData, quantities: newQuantities });
  };

  const handleFiles = useCallback((files) => {
    const validFiles = files.filter(
      (file) => file.type.match("image.*") && file.size <= 5 * 1024 * 1024
    );

    if (validFiles.length === 0) {
      setError(
        "No valid images selected. Ensure files are images and under 5MB."
      );
      return;
    }

    if (validFiles.length + formData.images.length + existingImages.length > 10) {
      setError("You can upload a maximum of 10 images per product");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...validFiles],
    }));

    setImagePreviews((prev) => [
      ...prev,
      ...validFiles.map((file) => URL.createObjectURL(file)),
    ]);
  }, [formData.images, existingImages]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        const files = Array.from(e.dataTransfer.files).filter((file) =>
          file.type.match("image.*")
        );
        handleFiles(files);
      }
    },
    [handleFiles]
  );

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const files = Array.from(e.target.files);
      handleFiles(files);
    }
  };

  const removeImage = (index) => {
    if (index < existingImages.length) {
      setImagesToDelete([...imagesToDelete, existingImages[index]]);
      const newExistingImages = [...existingImages];
      newExistingImages.splice(index, 1);
      setExistingImages(newExistingImages);
      
      const newPreviews = [...imagePreviews];
      newPreviews.splice(index, 1);
      setImagePreviews(newPreviews);
    } else {
      const adjustedIndex = index - existingImages.length;
      const newImages = [...formData.images];
      newImages.splice(adjustedIndex, 1);
      setFormData({ ...formData, images: newImages });

      const newPreviews = [...imagePreviews];
      URL.revokeObjectURL(newPreviews[index]);
      newPreviews.splice(index, 1);
      setImagePreviews(newPreviews);
    }
  };

  const uploadImages = async (images, productId) => {
    if (!productId) {
      throw new Error("Invalid product ID");
    }
    if (!images.every((image) => image.name)) {
      throw new Error("One or more images are missing a valid name");
    }

    const imageUrls = [];
    for (const image of images) {
      try {
        const sanitizedFileName = image.name.replace(/[^a-zA-Z0-9.-]/g, "_");
        const timestamp = Date.now();
        const imageRef = storageRef(
          storage,
          `Architects/${architectId}/Products/${formData.category}/${productId}/${timestamp}_${sanitizedFileName}`
        );

        await uploadBytes(imageRef, image);
        const url = await getDownloadURL(imageRef);
        imageUrls.push(url);
      } catch (uploadError) {
        console.error("Image upload error:", uploadError);
        throw new Error(
          `Failed to upload image ${image.name}: ${uploadError.message}`
        );
      }
    }
    return imageUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      if (!architectId) {
        throw new Error(
          "Business information is missing. Please ensure you are logged in."
        );
      }

      // Validate custom category if selected
      let finalCategory = formData.category;
      if (showCustomCategory) {
        const validation = validateCustomCategory(formData.customCategory);
        if (!validation.isValid) {
          throw new Error(validation.message);
        }
        finalCategory = validation.formattedValue;
      }

      // Validate product name
      const nameValidation = validateProductName(formData.productName);
      if (!nameValidation.isValid) {
        throw new Error(nameValidation.message);
      }

      // Validate required fields
      const requiredFields = {
        material: "Material is required",
        color: "Color is required",
        size: "Size is required",
      };

      const missingFields = Object.keys(requiredFields).filter(
        field => !formData[field]
      );

      if (missingFields.length > 0) {
        throw new Error(requiredFields[missingFields[0]]);
      }

      // Validate quantity fields
      const quantityValidation = validateQuantityFields(formData.quantities);
      if (!quantityValidation.isValid) {
        setFieldErrors(quantityValidation.errors);
        throw new Error("Please correct the pricing information");
      }

      // Validate images
      if (imagePreviews.length + existingImages.length === 0) {
        throw new Error("At least one product image is required");
      }

      let productData = {
        category: showCustomCategory 
          ? finalCategory 
          : predefinedCategories.find((cat) => cat.value === formData.category)?.label || formData.category,
        categoryKey: finalCategory,
        productName: formData.productName,
        material: formData.material,
        color: formData.color,
        size: formData.size,
        theme: formData.theme || "",
        isHandmade: formData.isHandmade,
        isAntique: formData.isAntique,
        isEcoFriendly: formData.isEcoFriendly,
        description: formData.description || "",
        sellerEmail: sellerEmail || "",
      };

      // Add architect reference if exists
      if (architectId) {
        productData.architectId = architectId;
      }

      if (formData.quantities.length === 1) {
        productData.quantity = parseInt(formData.quantities[0].quantity) || 0;
        productData.price = parseFloat(formData.quantities[0].price) || 0;
        productData.discountPrice = parseFloat(formData.quantities[0].discountPrice) || 0;
      } else {
        productData.variants = formData.quantities.map(qty => ({
          quantity: parseInt(qty.quantity) || 0,
          price: parseFloat(qty.price) || 0,
          discountPrice: parseFloat(qty.discountPrice) || 0,
        }));
      }

      const productId = formData.productName.trim().toLowerCase().replace(/\s+/g, '-');
      
      // Determine the database path
      let dbPath = `Architects/${architectId}/Products/${finalCategory}/${isEditMode ? productToEdit.id : productId}`;
      
      const productRef = ref(db, dbPath);

      let imageUrls = [...existingImages];
      
      if (imagesToDelete.length > 0) {
        await Promise.all(
          imagesToDelete.map(async (imageUrl) => {
            try {
              const imageRef = storageRef(storage, imageUrl);
              await deleteObject(imageRef);
              imageUrls = imageUrls.filter(url => url !== imageUrl);
            } catch (err) {
              console.error("Error deleting image:", err);
            }
          })
        );
      }

      if (formData.images.length > 0) {
        const newImageUrls = await uploadImages(formData.images, isEditMode ? productToEdit.id : productId);
        imageUrls = [...imageUrls, ...newImageUrls];
      }

      if (imageUrls.length > 0) {
        productData.images = imageUrls;
      } else if (isEditMode) {
        productData.images = [];
      }

      if (isEditMode) {
        await update(productRef, productData);
        setSuccess("Product updated successfully!");
        setTimeout(() => {
          navigate('/architecture/products/view');
        }, 1500);
      } else {
        await set(productRef, {
          ...productData,
          createdAt: serverTimestamp(),
        });
        setSuccess("Product added successfully!");
      }

      if (!isEditMode) {
        setFormData({
          category: "",
          customCategory: "",
          productName: "",
          material: "",
          color: "",
          size: "",
          theme: "",
          isHandmade: false,
          isAntique: false,
          isEcoFriendly: false,
          quantities: [
            {
              quantity: "",
              price: "",
              discountPrice: "",
            },
          ],
          description: "",
          images: [],
        });
        setImagePreviews([]);
        setExistingImages([]);
        setShowCustomCategory(false);
        setFieldErrors({});
      }
    } catch (err) {
      console.error("Error saving product:", err);
      setError(err.message || "Failed to save product. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const renderQuantityFields = () => {
    return (
      <div className="mb-6">
        <div className="flex justify-between items-center mb-3">
          <label className="block text-purple-800 text-sm font-bold">
            Pricing & Inventory
          </label>
          <button
            type="button"
            onClick={addQuantityField}
            className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-1 px-3 rounded-lg text-sm shadow-md transition-colors"
            disabled={formData.quantities.length >= 5}
          >
            + Add Variant
          </button>
        </div>
        {formData.quantities.map((qty, index) => (
          <div key={index} className="grid grid-cols-3 gap-4 mb-3 items-end">
            <div>
              <label className="block text-purple-700 text-xs mb-1">
                Quantity
              </label>
              <input
                className={`shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                  fieldErrors[`quantity-${index}`] ? 'border-red-500' : ''
                }`}
                name="quantity"
                type="number"
                min="1"
                value={qty.quantity}
                onChange={(e) => handleQuantityChange(index, e)}
                required
              />
              {fieldErrors[`quantity-${index}`] && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors[`quantity-${index}`]}</p>
              )}
            </div>
            <div>
              <label className="block text-purple-700 text-xs mb-1">
                Price (₹)
              </label>
              <input
                className={`shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                  fieldErrors[`price-${index}`] ? 'border-red-500' : ''
                }`}
                name="price"
                type="number"
                min="0.01"
                step="0.01"
                value={qty.price}
                onChange={(e) => handleQuantityChange(index, e)}
                required
              />
              {fieldErrors[`price-${index}`] && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors[`price-${index}`]}</p>
              )}
            </div>
            <div className="flex items-center">
              <div className="flex-1">
                <label className="block text-purple-700 text-xs mb-1">
                  Discount Price (₹)
                </label>
                <input
                  className={`shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                    fieldErrors[`discountPrice-${index}`] ? 'border-red-500' : ''
                  }`}
                  name="discountPrice"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={qty.discountPrice}
                  onChange={(e) => handleQuantityChange(index, e)}
                  placeholder="Optional"
                />
                {fieldErrors[`discountPrice-${index}`] && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors[`discountPrice-${index}`]}</p>
                )}
              </div>
              {formData.quantities.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeQuantityField(index)}
                  className="ml-2 text-red-500 hover:text-red-700 bg-red-50 rounded-full p-1"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        ))}
        {formData.quantities.length >= 5 && (
          <p className="text-xs text-purple-600 mt-1">Maximum of 5 variants allowed per product</p>
        )}
      </div>
    );
  };

  const renderFormFields = () => {
    return (
      <>
        <div className="mb-6 bg-purple-50 p-4 rounded-lg border border-purple-200">
          <h3 className="text-sm font-bold text-purple-800 mb-2">Product Naming Guidelines</h3>
          <ul className="text-xs text-purple-700 list-disc pl-5 space-y-1">
            <li>Use descriptive names (e.g., "Handcrafted Wooden Ganesha Statue")</li>
            <li>Include key details like material or size when relevant</li>
            <li>Avoid special characters: . $ # [ ] /</li>
          </ul>
        </div>
        <div className="mb-4">
          <label
            className="block text-purple-800 text-sm font-bold mb-2"
            htmlFor="productName"
          >
            Product Name
          </label>
          <input
            className={`shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
              fieldErrors.productName ? 'border-red-500' : ''
            }`}
            id="productName"
            name="productName"
            type="text"
            value={formData.productName}
            onChange={handleInputChange}
            required
            placeholder="e.g., Handcrafted Wooden Ganesha Statue"
          />
          {fieldErrors.productName ? (
            <p className="text-red-500 text-xs mt-1">{fieldErrors.productName}</p>
          ) : (
            <p className="text-xs text-purple-600 mt-1">
              Avoid using these characters: . $ # [ ] /
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label
              className="block text-purple-800 text-sm font-bold mb-2"
              htmlFor="material"
            >
              Material
            </label>
            <select
              className={`shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                fieldErrors.material ? 'border-red-500' : ''
              }`}
              id="material"
              name="material"
              value={formData.material}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Material</option>
              {materials.map(material => (
                <option key={material} value={material}>{material}</option>
              ))}
            </select>
            {fieldErrors.material && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.material}</p>
            )}
          </div>
          <div>
            <label
              className="block text-purple-800 text-sm font-bold mb-2"
              htmlFor="color"
            >
              Color/Finish
            </label>
            <select
              className={`shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                fieldErrors.color ? 'border-red-500' : ''
              }`}
              id="color"
              name="color"
              value={formData.color}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Color/Finish</option>
              {colors.map(color => (
                <option key={color} value={color}>{color}</option>
              ))}
            </select>
            {fieldErrors.color && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.color}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label
              className="block text-purple-800 text-sm font-bold mb-2"
              htmlFor="size"
            >
              Size
            </label>
            <select
              className={`shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                fieldErrors.size ? 'border-red-500' : ''
              }`}
              id="size"
              name="size"
              value={formData.size}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Size</option>
              {sizes.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            {fieldErrors.size && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.size}</p>
            )}
          </div>
          <div>
            <label
              className="block text-purple-800 text-sm font-bold mb-2"
              htmlFor="theme"
            >
              Theme (Optional)
            </label>
            <input
              className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              id="theme"
              name="theme"
              type="text"
              value={formData.theme}
              onChange={handleInputChange}
              placeholder="e.g., Traditional, Modern, Spiritual"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isHandmade"
              name="isHandmade"
              checked={formData.isHandmade}
              onChange={handleInputChange}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
            <label htmlFor="isHandmade" className="ml-2 block text-sm text-purple-700">
              Handmade
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isAntique"
              name="isAntique"
              checked={formData.isAntique}
              onChange={handleInputChange}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
            <label htmlFor="isAntique" className="ml-2 block text-sm text-purple-700">
              Antique/Vintage
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isEcoFriendly"
              name="isEcoFriendly"
              checked={formData.isEcoFriendly}
              onChange={handleInputChange}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
            <label htmlFor="isEcoFriendly" className="ml-2 block text-sm text-purple-700">
              Eco-Friendly
            </label>
          </div>
        </div>

        {renderQuantityFields()}
      </>
    );
  };

  const renderCategorySelection = () => (
    <div className="mb-6">
      <label
        className="block text-purple-800 text-sm font-bold mb-2"
        htmlFor="category"
      >
        Category
      </label>
      <select
        className="shadow appearance-none border rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
        id="category"
        name="category"
        value={formData.category}
        onChange={handleCategoryChange}
        required
        disabled={isEditMode}
      >
        <option value="">Select a category</option>
        {predefinedCategories.map((category) => (
          <option key={category.value} value={category.value}>
            {category.label}
          </option>
        ))}
        <option value="custom">+ Add Custom Category</option>
      </select>
      
      {showCustomCategory && (
        <div className="mt-4">
          <label
            className="block text-purple-800 text-sm font-bold mb-2"
            htmlFor="customCategory"
          >
            Custom Category Name
          </label>
          <input
            className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            id="customCategory"
            name="customCategory"
            type="text"
            value={formData.customCategory}
            onChange={handleCustomCategoryChange}
            required={showCustomCategory}
            placeholder="Enter your custom category name"
          />
          <p className="text-xs text-purple-600 mt-1">
            First letter of each word will be capitalized automatically. Avoid special characters.
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 px-6 py-4 shadow-md">
        <div className="max-w-7xl mx-auto flex items-center">
          <h1 className="text-2xl font-bold text-white">
            <span className="text-purple-200">Products</span>
            <span className="mx-2 text-white">/</span>
            <span className="text-white">{isEditMode ? 'Edit Product' : 'Add Product'}</span>
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-purple-100">
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 px-6 py-4 border-b border-purple-200">
            <h2 className="text-xl font-bold text-purple-800 flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 2a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0V6H8a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1H8a1 1 0 010-2h1v-1a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
              {isEditMode ? 'Edit Product' : 'Add New Product'}
            </h2>
          </div>

          <div className="p-6">
            {error && (
              <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
                {error}
              </div>
            )}
            {success && (
              <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">
                {success}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              {renderCategorySelection()}
              
              {formData.category && renderFormFields()}

              {formData.category && (
                <>
                  <div className="mb-6">
                    <label
                      className="block text-purple-800 text-sm font-bold mb-2"
                      htmlFor="description"
                    >
                      Product Description
                    </label>
                    <textarea
                      className="shadow appearance-none border rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 h-32"
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Describe the product in detail, including any special features, craftsmanship, or cultural significance..."
                    />
                  </div>

                  <div className="mb-8">
                    <label className="block text-purple-800 text-sm font-bold mb-2">
                      Product Images
                    </label>

                    <div
                      className={`border-2 border-dashed rounded-lg p-8 text-center mb-4 transition-colors ${
                        dragActive
                          ? "border-purple-400 bg-purple-50"
                          : "border-purple-200 bg-purple-50"
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                    >
                      <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        accept="image/*"
                        className="hidden"
                        id="product-images"
                        disabled={uploading}
                      />
                      <label
                        htmlFor="product-images"
                        className={`cursor-pointer flex flex-col items-center justify-center ${
                          uploading ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        <svg
                          className="w-12 h-12 text-purple-400 mb-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="text-purple-700 font-medium">
                          Drag & drop images here, or click to select files
                        </p>
                        <p className="text-purple-500 text-sm mt-1">
                          Maximum file size: 5MB. Accepted formats: JPEG, PNG,
                          GIF. Max 10 images.
                        </p>
                      </label>
                    </div>

                    {(imagePreviews.length > 0 || existingImages.length > 0) && (
                      <div className="mt-6">
                        <h3 className="text-sm font-medium text-purple-700 mb-3">
                          Selected Images ({imagePreviews.length + existingImages.length}):
                        </h3>
                        <div className="flex flex-wrap gap-4">
                          {imagePreviews.map((preview, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={preview}
                                alt={`Preview ${index}`}
                                className="h-24 w-24 object-cover rounded-lg border-2 border-purple-200 shadow-sm"
                              />
                              <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-opacity opacity-0 group-hover:opacity-100 shadow-md"
                                title="Remove image"
                                disabled={uploading}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-4">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-3 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 shadow-md transition-all duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-bold py-3 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 shadow-md transition-all duration-200 ${
                        uploading ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      disabled={uploading}
                    >
                      {uploading 
                        ? isEditMode 
                          ? "Updating Product..." 
                          : "Adding Product..."
                        : isEditMode
                          ? "Update Product"
                          : "Add Product"}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDecorativeProductForm;